import { Button, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { IInventoryOption } from '../../Interfaces/IInventoryOption';
import { ILegalTransactionRelationshipRealEstate } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { ILegalTransactionRelationshipRealEstatePropertyType } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstatePropertyType';
import { IPropertyType } from '../../Interfaces/IPropertyType';
import { IPurchaseContractRelationType } from '../../Interfaces/IPurchaseContractRelationType';
import { IRealEstateUseType } from '../../Interfaces/IRealEstateUseType';
import { removeFirstManager } from './utilities';

import { Delete, Edit } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import {
  DataGrid,
  deDE,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { CreateRealEstateDialog } from './RealEstate/CreateRealEstate/CreateRealEstateDialog';
import { EditRealEstateDialog } from './RealEstate/EditRealEstateDialog';

interface IProps {
  relationship: ILegalTransactionRelationship;
  propertyTypes: IPropertyType[];
  inventoryOptions: IInventoryOption[];
  purchaseContractRelationTypes: IPurchaseContractRelationType[];
  realEstateUseTypes: IRealEstateUseType[];
  setRelationship: (newValue: ILegalTransactionRelationship) => void;
  takenLegalClients: ILegalClient[];
  setTakenLegalClients: React.Dispatch<React.SetStateAction<ILegalClient[]>>;
}

const PAGE_SIZE = 5;

export const PurchaseContractRealEstatesMain: React.FC<IProps> = props => {
  const { relationship, propertyTypes, setRelationship } = props;

  const [editedRealEstate, setEditedRealEstate] =
    useState<ILegalTransactionRelationshipRealEstate>();
  const [isMount, setIsMount] = useState(false);

  const propertyTypeMap = useMemo(
    () =>
      new Map(propertyTypes.map(pt => [pt.idPropertyType, pt.PropertyType])),
    [propertyTypes]
  );

  const issuePropertyTypes = useCallback(
    (
      realEstatePropertyTypes:
        | ILegalTransactionRelationshipRealEstatePropertyType[]
        | undefined
        | null
    ) =>
      Array.isArray(realEstatePropertyTypes)
        ? realEstatePropertyTypes
            .map(t => propertyTypeMap.get(t.idPropertyType))
            .join(', ')
        : '',
    [propertyTypeMap]
  );

  const columns: GridColDef[] = [
    {
      field: 'idRealEstate',
      headerName: 'ID',
      width: 90,
      valueGetter: ({
        row: { idRealEstate },
      }: GridValueGetterParams<ILegalTransactionRelationshipRealEstate>) =>
        idRealEstate > 0 ? idRealEstate : 'Neu',
    },
    {
      field: 'Price',
      headerName: 'Kaufpreis',
      width: 150,
      valueGetter: ({
        row: { Price },
      }: GridValueGetterParams<ILegalTransactionRelationshipRealEstate>) =>
        Number(Price).toLocaleString('de-DE', {
          style: 'currency',
          currency: 'EUR',
        }),
    },
    {
      field: 'PropertyType',
      headerName: 'Grundstücksarten',
      width: 250,
      valueGetter: ({
        row: { PropertyTypeArray },
      }: GridValueGetterParams<ILegalTransactionRelationshipRealEstate>) =>
        issuePropertyTypes(PropertyTypeArray),
    },
    {
      field: 'address',
      headerName: 'Adresse',
      flex: 1,
      valueGetter: ({
        row: {
          Street,
          StreetNr,
          StreetAditional,
          City,
          Postcode,
          Longitude,
          Latitude,
        },
      }: GridValueGetterParams<ILegalTransactionRelationshipRealEstate>) =>
        Street && StreetNr && City && Postcode
          ? `${Street} ${StreetNr} ${StreetAditional ?? ''}, ${City} ${Postcode}`
          : Longitude && Latitude
            ? `${Longitude} ${Latitude}`
            : '',
    },
    {
      field: 'Actions',
      headerName: 'Aktionen',
      width: 150,
      renderCell: ({
        row,
      }: GridRenderCellParams<ILegalTransactionRelationshipRealEstate>) => (
        <>
          <IconButton
            onClick={() => {
              setEditedRealEstate(row);
              setIsMount(true);
            }}
          >
            <Edit />
          </IconButton>
          <IconButton
            onClick={() => {
              // Remove the old manager from the relationship if there was one
              let otherManagers = [...relationship.Members];

              if (row.BuildingManager_idLegalClient) {
                otherManagers = removeFirstManager(
                  relationship.Members,
                  row.BuildingManager_idLegalClient
                );
              }

              setRelationship({
                ...relationship,
                RealEstateArray: relationship.RealEstateArray.filter(
                  x => x.idRealEstate !== row.idRealEstate
                ),
                Members: otherManagers,
              });
            }}
          >
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ mt: 5 }} />
      <Typography variant='h5' sx={{ mb: 3 }}>
        Erfassung der Vertragsgegenstände
        <Button
          sx={{ float: 'right' }}
          variant='contained'
          onClick={() => setIsMount(true)}
        >
          Objekt hinzufügen
        </Button>
      </Typography>
      <Box sx={{ maxHeight: 400, width: '100%' }}>
        <DataGrid
          sx={{ mt: 2 }}
          autoHeight
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          rows={relationship.RealEstateArray}
          columns={columns}
          getRowId={row => row.idRealEstate}
          disableRowSelectionOnClick
          pagination={
            relationship.RealEstateArray.length > PAGE_SIZE || undefined
          }
          pageSizeOptions={[PAGE_SIZE]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: PAGE_SIZE,
              },
            },
          }}
        />
      </Box>

      {isMount && editedRealEstate && (
        <EditRealEstateDialog
          {...props}
          editedRealEstate={editedRealEstate}
          unmount={() => {
            setIsMount(false);
            setEditedRealEstate(undefined);
          }}
        />
      )}
      {isMount && !editedRealEstate && (
        <CreateRealEstateDialog {...props} unmount={() => setIsMount(false)} />
      )}
    </>
  );
};
